import React, { useCallback, useRef, useState } from 'react';
import cl from 'classnames';
import { useTranslation } from 'react-i18next';

import { IconsEnum } from '../../../../../../../assets/icons/types';
import { Icon } from '../../../../../../../helpers/Icon';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { StaticImageHelper } from '../../../../../../../helpers/StaticImageHelper';
import { LoadingSpinner } from '../../../../../../../helpers/LoadingSpinner';
import { TextareaAutosizeInput } from '../../../../../../../helpers/TextareaAutosizeInput';
import { Translate } from '../../../../../../../helpers/Translate';
import { CommandHeader } from '../../../../../../../helpers/CommandHeader';
import { CommandDropdown } from '../../../../../../../helpers/CommandDropdown';

import { modelViewersKeys, words } from '../../../../../../../locales/keys';
import { LifestyleGenerationMode } from '../../../../../../lifestyleGenerations/lifestyleGenerationsTypes';
import { useSlashCommandInput } from '../../../../../../../utils/useSlashCommandInput';

interface ViewerCommand {
  id: LifestyleGenerationMode;
  alias: string;
  title: string;
  description: string;
}

interface PromptCardReferenceItem {
  id: string;
  localUrl: string;
  isUploading: boolean;
}

interface ModelViewerLifestylePromptCardProps {
  prompt: string;
  selectedCommand?: LifestyleGenerationMode | null;
  referenceFileLocalUrl?: string | null;
  isUploadingReference: boolean;
  isGenerating: boolean;
  generateError: string | null;
  onPromptChange: (value: string) => void;
  onSelectCommand?: (mode: LifestyleGenerationMode) => void;
  onClearCommand?: () => void;
  onReferenceFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onReferenceFileDrop: (file: File) => void;
  onClearReference?: () => void;
  onGenerate: () => void;
  allowedCommands?: LifestyleGenerationMode[];
  onMinimize?: () => void;
  onOpenConfigurator?: () => void;
  i18nPromptPlaceholder?: string;
  i18nGenerateText?: string;
  i18nAddReferenceText?: string;
  // Multi-reference mode — when `references` is provided it supersedes the
  // single referenceFileLocalUrl/onClearReference pair.
  references?: PromptCardReferenceItem[];
  onRemoveReference?: (id: string) => void;
  allowMultipleReferences?: boolean;
}

function ModelViewerLifestylePromptCard({
  prompt,
  selectedCommand,
  referenceFileLocalUrl,
  isUploadingReference,
  isGenerating,
  generateError,
  onPromptChange,
  onSelectCommand,
  onClearCommand,
  onReferenceFileChange,
  onReferenceFileDrop,
  onClearReference,
  onGenerate,
  allowedCommands = [],
  onMinimize,
  onOpenConfigurator,
  i18nPromptPlaceholder,
  i18nGenerateText,
  i18nAddReferenceText,
  references,
  onRemoveReference,
  allowMultipleReferences
}: ModelViewerLifestylePromptCardProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isReferenceDragging, setIsReferenceDragging] = useState(false);
  const referenceDragCounter = useRef(0);

  const handleReferenceDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    referenceDragCounter.current++;
    setIsReferenceDragging(true);
  }, []);

  const handleReferenceDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleReferenceDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    referenceDragCounter.current--;
    if (referenceDragCounter.current <= 0) {
      referenceDragCounter.current = 0;
      setIsReferenceDragging(false);
    }
  }, []);

  const handleReferenceDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      referenceDragCounter.current = 0;
      setIsReferenceDragging(false);
      const imageFiles = Array.from(e.dataTransfer.files ?? []).filter((file) =>
        file.type.startsWith('image/')
      );
      (allowMultipleReferences ? imageFiles : imageFiles.slice(0, 1)).forEach(
        (file) => onReferenceFileDrop(file)
      );
    },
    [onReferenceFileDrop, allowMultipleReferences]
  );

  // Each command is gated by its own permission; only commands the user is
  // allowed to use (passed via allowedCommands) are shown in the dropdown.
  const allViewerCommands: ViewerCommand[] = [
    // Product-only command: generates from model_id + scene metadata instead
    // of a screenshot. Picks by_reference / by_prompt by reference presence,
    // like Lifestyle.
    {
      id: LifestyleGenerationMode.ProductLifestyle,
      alias: 'studio-lifestyle',
      title: t(modelViewersKeys.createProductLifestyleShort),
      description: t(modelViewersKeys.createProductLifestyle)
    },
    {
      id: LifestyleGenerationMode.Lifestyle,
      alias: 'lifestyle',
      title: t(modelViewersKeys.createLifestyle),
      description: t(modelViewersKeys.lifestyleSceneDescription)
    },
    {
      id: LifestyleGenerationMode.Silo,
      alias: 'silo',
      title: t(modelViewersKeys.createSilo),
      description: t(modelViewersKeys.siloSceneDescription)
    },
    {
      id: LifestyleGenerationMode.CloseUp,
      alias: 'close-up',
      title: t(modelViewersKeys.createCloseUp),
      description: t(modelViewersKeys.closeUpSceneDescription)
    }
  ];

  const viewerCommands = allViewerCommands.filter((command) =>
    allowedCommands.includes(command.id)
  );

  const {
    showDropdown,
    filteredCommands,
    handleChange: slashHandleChange,
    handleOpenDropdown,
    handleSelectCommand: slashHandleSelect,
    handleCloseDropdown,
    handleKeyDown: slashHandleKeyDown
  } = useSlashCommandInput<ViewerCommand>(viewerCommands);

  const isDisabled = isGenerating || isUploadingReference;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
      handleCloseDropdown();
    }
  };

  const handlePromptChange = (value: string) => {
    const next = slashHandleChange(value);
    onPromptChange(next);
  };

  const handleCommandSelect = (cmd: ViewerCommand) => {
    const clearedValue = slashHandleSelect(cmd, prompt);
    onPromptChange(clearedValue);
    onSelectCommand?.(cmd.id);
  };

  const activeCommand = selectedCommand
    ? viewerCommands.find((c) => c.id === selectedCommand)
    : null;

  const referenceItems: PromptCardReferenceItem[] =
    references ??
    (referenceFileLocalUrl
      ? [
          {
            id: 'reference',
            localUrl: referenceFileLocalUrl,
            isUploading: isUploadingReference
          }
        ]
      : []);

  const handleRemoveReferenceItem = (id: string) =>
    references ? onRemoveReference?.(id) : onClearReference?.();

  return (
    <div
      ref={containerRef}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onDragEnter={handleReferenceDragEnter}
      onDragOver={handleReferenceDragOver}
      onDragLeave={handleReferenceDragLeave}
      onDrop={handleReferenceDrop}
      className={cl(
        'group/message-editor border rounded-lg absolute bottom-16 left-1/2 -translate-x-1/2 w-[min(520px,calc(100%-6rem))] bg-white/75 dark:bg-gray-900/75 backdrop-blur-sm z-1',
        selectedCommand
          ? 'border-purple-600/60 dark:border-purple-600/60'
          : isFocused
          ? 'ring-4 ring-purple-500/20 border-purple-400 dark:border-purple-500'
          : isReferenceDragging
          ? 'ring-4 ring-purple-500/20 border-purple-400 dark:border-purple-500'
          : 'border-gray-200 dark:border-gray-700'
      )}
    >
      {isReferenceDragging && (
        <div className="absolute inset-0 z-10 rounded-lg flex items-center justify-center bg-purple-500/10 border-2 border-dashed border-purple-400 dark:border-purple-500 pointer-events-none">
          <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
            <Translate
              id={i18nAddReferenceText ?? modelViewersKeys.addReference}
            />
          </p>
        </div>
      )}
      {onOpenConfigurator && (
        <PureIconButtonHelper
          icon={IconsEnum.CUBE_TRANSPARENT_OUTLINE}
          iconClassName="h-5 w-5"
          i18nText={modelViewersKeys.openConfigurator}
          className="absolute right-full top-1/2 -translate-y-1/2 mr-2 flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white/75 dark:bg-gray-900/75 backdrop-blur-sm border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-400 px-2.5 py-2 rounded-xl shadow-md whitespace-nowrap transition-colors duration-150"
          onClick={onOpenConfigurator}
        />
      )}

      {onMinimize && (
        <div className="absolute bottom-full left-0 right-0 flex justify-end items-end pb-1 pointer-events-none">
          <PureIconButtonHelper
            icon={IconsEnum.WINDOW_MINIMIZE_OUTLINE}
            iconClassName="h-5 w-5"
            className="py-1 pl-1 pr-1 rounded-md inline-flex items-center leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 scale-0 group-hover/message-editor:scale-100 transition-transform duration-100 pointer-events-auto transition-colors duration-150 ease-out"
            onClick={onMinimize}
          />
        </div>
      )}

      <div className="relative grid grid-cols-[auto_1fr_auto_auto]">
        {/* Command dropdown */}
        {showDropdown && (
          <CommandDropdown<ViewerCommand>
            items={filteredCommands}
            onSelect={handleCommandSelect}
          />
        )}

        {/* Active command header */}
        {activeCommand && (
          <CommandHeader
            title={activeCommand.title}
            alias={activeCommand.alias}
            onClear={() => onClearCommand?.()}
          />
        )}

        <div className="col-span-full">
          <TextareaAutosizeInput
            name="lifestyle-prompt"
            value={prompt}
            onChange={handlePromptChange}
            i18nPlaceholder={
              i18nPromptPlaceholder ??
              modelViewersKeys.lifestylePromptPlaceholder
            }
            maxRows={5}
            className="min-h-7 max-h-40 w-full resize-none border-none bg-transparent focus:ring-0 focus:outline-none pl-3 pr-2 pt-2 pb-0 text-sm leading-snug text-gray-700 dark:text-gray-300 placeholder-gray-500 dark:placeholder-gray-500"
            onKeyDown={slashHandleKeyDown}
          />
        </div>

        {generateError && (
          <p className="col-span-full px-3 pb-1 text-xs text-red-500 dark:text-red-400">
            {generateError}
          </p>
        )}

        <div className="flex flex-wrap gap-0.5 flex-1 items-center pl-2 min-h-10">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple={allowMultipleReferences}
            className="sr-only"
            onChange={onReferenceFileChange}
          />
          <PureIconButtonHelper
            icon={IconsEnum.PHOTOGRAPH_OUTLINE}
            iconClassName="h-5 w-5"
            i18nText={i18nAddReferenceText ?? modelViewersKeys.addReference}
            className="py-1 pl-1 pr-2 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 focus:ring-offset-0 transition-colors duration-150 ease-out"
            onClick={() => fileInputRef.current?.click()}
          />

          {referenceItems.map((item) => (
            <div
              key={item.id}
              className="relative w-7 h-7 rounded overflow-hidden border dark:border-gray-700 flex-shrink-0 group"
            >
              {item.isUploading ? (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <PureIconButtonHelper
                  icon={IconsEnum.X_OUTLINE}
                  iconClassName="h-3 w-3 text-white"
                  className="absolute inset-0 bg-black/50 items-center justify-center z-10 hidden group-hover:flex"
                  onClick={() => handleRemoveReferenceItem(item.id)}
                />
              )}
              <StaticImageHelper
                src={item.localUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ))}

          {viewerCommands.length > 0 && (
            <PureIconButtonHelper
              icon={IconsEnum.COMMAND_OUTLINE}
              iconClassName="h-5 w-5"
              i18nText={modelViewersKeys.commands}
              className="py-1 pl-1 pr-2 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 focus:ring-offset-0 transition-colors duration-150 ease-out"
              onClick={handleOpenDropdown}
            />
          )}
        </div>

        <div className="flex-1 h-full cursor-text" aria-hidden="true" />

        <div className="col-span-2 flex shrink-0 justify-end items-center h-10 pr-1.5 gap-2">
          {selectedCommand ? (
            <ButtonHelper
              disabled={isDisabled}
              onClick={onGenerate}
              className={cl(
                'py-1 pl-1 pr-2 space-x-1 rounded-md inline-flex items-center text-sm font-medium leading-5 focus:ring-base text-white border-cosmos animate-gradient-angle shadow-sm hover:shadow-md transition-colors duration-150 ease-out',
                isDisabled
                  ? 'bg-black/40 dark:bg-black/60 cursor-not-allowed'
                  : 'bg-white/15 dark:bg-black/70 hover:bg-white/5 dark:hover:bg-black/25'
              )}
            >
              {isGenerating ? (
                <LoadingSpinner size="md" />
              ) : (
                <Icon className="h-5 w-5" icon={IconsEnum.SPARKLES_SOLID} />
              )}
              <span>
                <Translate
                  id={
                    isGenerating
                      ? modelViewersKeys.generatingLifestyle
                      : words.create
                  }
                />
              </span>
            </ButtonHelper>
          ) : (
            <ButtonHelper
              disabled={isDisabled}
              onClick={onGenerate}
              className={cl(
                'py-1 pl-1 pr-2 space-x-1 rounded-md inline-flex items-center text-sm font-medium leading-5 focus:ring-base text-white border-cosmos animate-gradient-angle shadow-sm hover:shadow-md transition-colors duration-150 ease-out',
                isDisabled
                  ? 'bg-black/40 dark:bg-black/60 cursor-not-allowed'
                  : 'bg-white/15 dark:bg-black/70 hover:bg-white/5 dark:hover:bg-black/25'
              )}
            >
              {isGenerating ? (
                <LoadingSpinner size="md" />
              ) : (
                <Icon className="h-5 w-5" icon={IconsEnum.SPARKLES_SOLID} />
              )}
              <span>
                <Translate
                  id={
                    isGenerating
                      ? modelViewersKeys.generatingLifestyle
                      : i18nGenerateText ?? modelViewersKeys.quickLifestyle
                  }
                />
              </span>
            </ButtonHelper>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModelViewerLifestylePromptCard;
